import { Observable, from } from 'rxjs'
import { PageActivity, SystemNotification, Id } from 'types/definitions'
import Conversation from './models/Conversation'
import Message from './models/Message'
import { withLatestFrom, filter, mergeMap, map, tap } from 'rxjs/operators'
import { unpair } from 'utils/rx-util'
import { FetchWorkspace } from 'iam/WorkspaceStore'

/**
 * Structure representing a message to show a system
 * notification for, along with the onClick handler
 * for clicking the notification.
 */
export interface MessageSystemNotification {
  message: Message
  onClick?: () => void
}

/**
 * Returns an observable for messages that we need to
 * send notifications for based on the active conversation
 * and the page activity status. We filter out call references
 * because calls have their own notifications.
 *
 * @param activeConversation$  an observable for the conversation considered "active".
 * We don't want to display notifications for that conversation unless the page is inactive.
 * @param newMessages$ an observable for all new incoming messages (realtime)
 * @param pageActivity$ an observable for the page's visibility.
 */
export function MessageNotificationsStream(
  activeConversation$: Observable<Conversation | null>,
  newMessages$: Observable<Message>,
  pageActivity$: Observable<PageActivity>
) {
  return newMessages$.pipe(
    filter(
      (message) =>
        message.sender.isCurrentUser === false &&
        message.content.type !== 'Call'
    ),
    withLatestFrom(pageActivity$, activeConversation$),
    filter(
      ([message, activity, convo]) =>
        activity === PageActivity.Inactive || convo !== message.conversation
    ),
    unpair()
  )
}

/**
 * Fetches the workspace before showing the system notification, since
 * the message-to-notification mapper uses the workspace name, we want to
 * make sure we have the workspace info fetched.
 */
export function MessageSystemNotificationsFlow(
  messageNotifications$: Observable<MessageSystemNotification>,
  fetchWorkspace: FetchWorkspace,
  sendSystemNotification: (notification: SystemNotification) => void
): Observable<unknown> {
  return messageNotifications$.pipe(
    mergeMap((x) =>
      from(fetchWorkspace(x.message.conversation.workspace.id)).pipe(
        map(() => x)
      )
    ),
    map(mapMessageToSystemNotification),
    tap(sendSystemNotification)
  )
}

/**
 * Maps a message to a system notification.
 *
 * @param message
 * @param onClick
 */
export function mapMessageToSystemNotification({
  message,
  onClick,
}: MessageSystemNotification): SystemNotification {
  const text =
    (message.content.type === 'Text' && message.content.text) || 'New message'
  return {
    onClick,
    title: `${message.sender.displayName} (${message.sender.workspace.name})`,
    body: text,
    tag: Id.assert(message.id),
    icon: message.sender.picture,
    timeout: 5000,
  }
}
